import { Injectable, effect, inject, signal } from '@angular/core';
import { FormGroup } from '@angular/forms';
import {
  DefaultHedges,
  Entity,
  GetForwardPointsResponse,
  IForwardPointsApiRequest,
  QuotePair,
  RfqSettingsState,
  RfqSetup,
  RfqTab
} from '@approot/models';
import {
  Account,
  AuthService,
  ConfigService,
  EntityMapperService,
  ExecutionContext,
  IRfqSetup,
  MarketDataService,
  ProductsConfigService,
  RfqService,
  SetupEventChange,
  SpotResponse,
  UserRfqPreferences
} from '@approot/services';
import {
  DateUtilities,
  Utilities,
  clone,
  isFeatureEnabled,
  reformatDate
} from '@approot/utilities';
import { components } from '@approot/utilities/digital-vega-schema';
import { Throttle } from 'lodash-decorators';
import { Observable, forkJoin, of, tap } from 'rxjs';
import {
  AdditionalRfqAttributes,
  HedgeRecord,
  MifidChecksStatus,
  createSetupCtrls
} from './rfq-setup';
export const defaultPremiumValues = ['Spot', 'Forward'];

export type SingleProviderDXState = 'SHOW_CLIENT' | 'SHOW_EXECUTE' | null;

@Injectable({
  providedIn: 'root'
})
export class RfqSetupService {
  public setupForm: FormGroup;
  public defaultHedges: DefaultHedges[] = [
    'None',
    'Spot',
    'Custom Spot',
    'Forward',
    'Custom Forward'
  ];
  public readonly hedges = signal<DefaultHedges[]>([]);
  public setup: IRfqSetup;
  public readonly whiteLabelPremiumStyles = [
    'Percent Base',
    'Percent Term',
    'Pips'
  ];
  public availableAccounts = signal<string[]>([]);
  public accountSet = signal<string>(null);
  public showPanel = signal<boolean>(false);
  public shouldMapPrimeBrokersViaEntities: boolean;
  public additionalRfqAttributes = signal<AdditionalRfqAttributes[]>([]);
  public mifidChecksStatus = signal<MifidChecksStatus>(null);
  private allEntities: Entity[] = [];
  public premiumValues = signal<string[]>([...defaultPremiumValues]);
  private entityMapperService = inject(EntityMapperService);
  private configService = inject(ConfigService);
  private rfqService = inject(RfqService);
  private authService = inject(AuthService);
  private executionContext: ExecutionContext;
  private marketDataService = inject(MarketDataService);
  private productsConfigService = inject(ProductsConfigService);
  constructor() {
    effect(() => {
      this.shouldMapPrimeBrokersViaEntities =
        !!this.configService.userSettings()?.parent[
          'rfq:mapPrimeBrokersViaEntities'
        ];
    });

    effect(() => {
      /* CA-CIB (only, at the moment) have all their PBs as entities (accounts).

      Medusa supplies PBs as {id: 13, name: "SC0000000697", shortName: "XYZY"} but that shortName might
      be wrong or have changed from CACIB's POV. We need to map the Medusa "name" to the entity "identifier"
      and then use the entity shortDisplayName in the form. Then need to do the reverse map before submitting
      the form.

      The flag for this logic is mapPrimeBrokersViaEntities (above). What a mess.
       */
      this.allEntities = this.entityMapperService.allEntities();
    });
  }

  public formatSetup(setup: RfqSetup): RfqSetup & {
    hedgeRateBid?: number;
    hedgeRateAsk?: number;
    hedgeRateMid?: number;
    hedges?: HedgeRecord[];
    isNew?: boolean;
    change?: SetupEventChange;
    legs?: any[];
    execution?: string;
  } {
    const setupForm = this.setupForm;
    const clonedSetup = clone(setup);
    if (clonedSetup.premiumDate) {
      clonedSetup.premiumDate = reformatDate(
        clonedSetup.premiumDate,
        'YYYY-MM-DD'
      );
    }
    if (clonedSetup.hedgeCustomAmount) {
      clonedSetup.hedgeCustomAmount = clonedSetup.hedgeCustomAmount.replace(
        /,/g,
        ''
      );
    }

    const hedgeRatesForm = setupForm.get('hedgeRates');
    if (setupForm.get('hedgeRate').disabled && hedgeRatesForm?.enabled) {
      delete clonedSetup.hedgeRate;
    }

    if (hedgeRatesForm?.enabled) {
      if (hedgeRatesForm.get('hedgeRateBid').enabled) {
        clonedSetup['hedgeRateBid'] = Utilities.cleanFormattedNumeric(
          hedgeRatesForm.get('hedgeRateBid').value
        );
      }

      if (hedgeRatesForm.get('hedgeRateAsk').enabled) {
        clonedSetup['hedgeRateAsk'] = Utilities.cleanFormattedNumeric(
          hedgeRatesForm.get('hedgeRateAsk').value
        );
      }

      if (hedgeRatesForm.get('hedgeRateMid')?.enabled) {
        clonedSetup['hedgeRateMid'] = Utilities.cleanFormattedNumeric(
          hedgeRatesForm.get('hedgeRateMid').value
        );
      }
    }

    delete clonedSetup.hedgeRates;
    return clonedSetup;
  }

  public setupValid(): boolean {
    return this.setup.valid;
  }

  public createSetupForm(
    loadedRfqSettings: RfqSettingsState,
    medusaAccountEntity: string,
    isVolBased: boolean,
    isLegacyWhitelabel: boolean,
    executionContext: ExecutionContext,
    isWhiteLabelUser: boolean
  ): void {
    this.executionContext = executionContext;
    const useBlockTrades = executionContext.useBlockTrades;
    const useSpotInRfqPanel = executionContext.useSpotInRfqPanel;
    const entity =
      loadedRfqSettings?.['rfq-details-settings']?.[medusaAccountEntity];
    const isGenericMultiHedge =
      executionContext.executionTypes[0].shortName === 'genericMultiHedge';
    const ctrls = createSetupCtrls(
      entity?.hedge,
      isVolBased,
      isLegacyWhitelabel,
      useBlockTrades,
      useSpotInRfqPanel,
      isWhiteLabelUser,
      executionContext.enableTwoWayCustomHedgeRateForNonDx,
      this.additionalRfqAttributes(),
      isGenericMultiHedge
    );
    this.setupForm = new FormGroup(ctrls);
    this.enableHedgeRate(executionContext);
  }

  public getPrimeBrokerFromForm() {
    let primeBroker: string = this.setupForm.get('primeBroker').value;
    if (this.shouldMapPrimeBrokersViaEntities) {
      primeBroker = this.reverseMapPrimeBrokersViaEntities(primeBroker);
    }
    return primeBroker;
  }

  public mapPrimeBrokersViaEntities(pbName: string, def: string) {
    if (!this.shouldMapPrimeBrokersViaEntities) {
      return pbName;
    }
    const pb = this.allEntities.find(e => e.identifier === pbName);
    return pb?.shortDisplayName || def;
  }

  public reverseMapPrimeBrokersViaEntities(shortDisplayName: string) {
    if (!this.shouldMapPrimeBrokersViaEntities) {
      return shortDisplayName;
    }
    const pb = this.allEntities.find(
      e => e.shortDisplayName === shortDisplayName
    );
    return pb?.identifier || shortDisplayName;
  }

  public defaultIndicativePremiumStyle(
    isNotionalBase: boolean,
    quotePair: QuotePair,
    blockSetValue = false
  ): void {
    if (!this.setupForm.get('indicativePremiumStyle') || blockSetValue) {
      return;
    }
    const premium = this.setupForm.get('premium').value;
    if (isNotionalBase) {
      this.setupForm
        .get('indicativePremiumStyle')
        .setValue(premium === quotePair.term ? 'Pips' : 'Percent Base');
    } else {
      this.setupForm.get('indicativePremiumStyle').setValue('Percent Term');
    }
  }

  public setAccountsNames(accounts: Account[]): void {
    const accountNames = accounts?.map(account => account.name);
    this.availableAccounts.update(
      _ =>
        accountNames
          .map(account => this.entityMapperService.getDisplayEntity(account))
          .sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase())) || []
    );
  }

  public updateAccountSet(account: string): void {
    this.accountSet.update(_ => account);
  }

  public isAccountPresent(): void {
    const accountCtrl = this.setupForm.get('account');
    if (!this.availableAccounts().includes(accountCtrl.value)) {
      accountCtrl.setValue(null);
      this.updateAccountSet(null);
    }
  }

  public setShowPanel(showPanel: boolean): void {
    this.showPanel.update(_ => showPanel);
  }

  public setPremiumValues(values: string[]): void {
    this.premiumValues.update(_ => values);
  }

  public spotOnlyPremiumValue(blockSetValue: boolean): void {
    this.setPremiumValues(['Spot']);
    if (!blockSetValue) {
      this.setupForm.get('premiumValue').setValue('Spot');
    }
  }

  public emptyTaskInputsAddAttr(): void {
    const apiCalls = {};
    const additionalAttributes = this.additionalRfqAttributes().filter(
      attribute => !attribute.taskInputs?.length && attribute.lambda
    );
    additionalAttributes.forEach(attribute => {
      this.setupForm.get(attribute.shortName)?.markAsPending();
      apiCalls[attribute.shortName] =
        this.rfqService.getAdditionalAttributesApi({
          lambda: attribute.lambda,
          payload: {
            task: attribute.task
          }
        });
    });
    this.additionalAttributesCall(apiCalls, additionalAttributes);
  }

  public genericAttributeListener(): void {
    Object.entries(this.setupForm.controls).forEach(([name, ctrl]) => {
      ctrl.valueChanges.subscribe(val => {
        this.wipeAdditionalAttribute(name);
        if (val) {
          this.onChangeInSetupAttribute(name);
        }
      });
    });
  }

  public setHedges(tabData: RfqTab): void {
    const executionContext = tabData.executionContext;
    const disableForwardHedge = executionContext.disableForwardHedge;

    if (executionContext.executionTypes[0].shortName === 'genericMultiHedge') {
      let hedges: DefaultHedges[] = [
        'Spot Hedge'
        // 'Custom Hedge'
      ];

      const tabShortName = tabData.title.productName.shortName;
      if (
        !(
          (tabShortName === 'vanillaStrip' ||
            tabShortName === 'vanillaStripMultiLeg' ||
            tabShortName === 'vanilla+VanillaStripMultiLeg') &&
          !isFeatureEnabled('enableLiveAutoHedgeForStrips', false)
        )
      ) {
        hedges.splice(0, 0, 'Live Auto Hedger');
      }

      if (!disableForwardHedge) {
        hedges.push('Forward Hedge');
      }
      this.hedges.set(hedges);
    } else {
      this.hedges.set([...this.defaultHedges]);
    }
  }

  public enableHedgeRatesForForwardHedge(
    executionContext: ExecutionContext,
    legSettlementDates: string[]
  ): void {
    const settlementDatesSame = legSettlementDates?.every(
      (val, _, arr) => val === arr[0]
    );

    if (
      executionContext.executionTypes[0]?.shortName === 'genericMultiHedge' &&
      this.setupForm.get('hedge').value === 'Forward Hedge'
    ) {
      this.setupForm.get('spotRefMethod').enable();
      if (settlementDatesSame) {
        this.setupForm.get('hedgeRates').enable();
        this.setupForm.get('execution').enable();
      } else {
        this.setupForm.get('execution').disable();
        this.setupForm.get('hedgeRates').disable();
      }
    }
  }

  public setLatestRefs(
    rfqTab: RfqTab,
    expiryDates: string[]
  ): IForwardPointsApiRequest | Observable<SpotResponse> {
    if (!rfqTab.title.quotePair) {
      return of(null);
    }
    const cross = `${
      rfqTab.title.quotePair.base + rfqTab.title.quotePair.term
    }`;
    if (!cross) {
      return of(null);
    }

    if (this.setupForm.get('hedge').value === 'Forward Hedge') {
      if (!expiryDates?.filter(expiryDate => expiryDate).length) {
        return of(null);
      }
      return this.getLatestFwdRef(expiryDates, cross).pipe(
        tap({
          next: (resp: GetForwardPointsResponse) => {
            const forwards =
              resp.forwards[DateUtilities.prettyDateToDate(expiryDates[0])]
                .outrightForward;
            this.setHedgesRates(forwards, cross);
          }
        })
      );
    } else {
      return this.marketDataService
        .calculateSpot({
          cross
        })
        .pipe(
          tap({
            next: (response: SpotResponse) =>
              this.setHedgesRates(response.spot, cross)
          })
        );
    }
  }

  public getLatestFwdRef(
    dates: string[],
    currencyPair: string
  ): IForwardPointsApiRequest {
    return this.marketDataService.getForwardPoints(dates, currencyPair);
  }

  private setHedgesRates(
    val: { ask: number; bid: number; mid: number },
    cross: string
  ): void {
    const dp = +this.productsConfigService.decimalPlacesForCross(cross);
    this.setupForm
      .get('hedgeRates')
      .get('hedgeRateAsk')
      .setValue(val.ask.toFixed(dp));
    this.setupForm
      .get('hedgeRates')
      .get('hedgeRateBid')
      .setValue(val.bid.toFixed(dp));
    this.setupForm
      .get('hedgeRates')
      .get('hedgeRateMid')
      .setValue(val.mid.toFixed(dp));
  }

  public updateHedges(updatedHedges: DefaultHedges[]): void {
    if (
      this.executionContext.executionTypes[0].shortName === 'genericMultiHedge'
    ) {
      return;
    }

    this.hedges.update(_ => updatedHedges);
  }

  private wipeAdditionalAttribute(shortName: string): void {
    const additionalAttributes = this.additionalRfqAttributes().filter(
      attribute => attribute.taskInputs?.includes(shortName) && attribute.lambda
    );

    additionalAttributes.forEach(attribute => {
      this.setupForm.get(attribute.shortName).setValue('');
    });
  }

  private onChangeInSetupAttribute(shortName: string): void {
    const apiCalls = {};
    const additionalAttributes = this.additionalRfqAttributes().filter(
      attribute => attribute.taskInputs?.includes(shortName) && attribute.lambda
    );

    additionalAttributes.forEach(attribute => {
      const controls = {};
      attribute.taskInputs.forEach(input => {
        controls[input] =
          input === 'account'
            ? this.entityMapperService.getRealEntity(
                this.setupForm.get(input).value
              )
            : this.setupForm.get(input).value;
      });
      this.setupForm.get(attribute.shortName).markAsPending();
      apiCalls[attribute.shortName] =
        this.rfqService.getAdditionalAttributesApi({
          lambda: attribute.lambda,
          payload: {
            task: attribute.task,
            ...controls
          }
        });
    });
    this.additionalAttributesCall(apiCalls, additionalAttributes);
  }

  private additionalAttributesCall(
    apiCalls: Record<string, Observable<Record<string, any>>>,
    additionalAttributes: AdditionalRfqAttributes[]
  ): void {
    forkJoin(apiCalls).subscribe({
      next: (response: Record<string, Record<string, any>>) => {
        Object.entries(response).forEach(([key, result]) => {
          const attribute = additionalAttributes.find(
            attribute => attribute.shortName === key
          );

          if (attribute.type === 'enum') {
            this.additionalRfqAttributes.update(state => {
              const attribute = state.find(attr => key === attr.shortName);
              attribute.values = result[attribute.taskOutputName] || [];
              return state;
            });
            this.setupForm.get(key).updateValueAndValidity();
          } else {
            this.setupForm.get(key).setValue(result[attribute.taskOutputName]);
          }
        });
      },
      error: _ => {
        additionalAttributes.forEach(attribute => {
          this.setupForm.get(attribute.shortName).updateValueAndValidity();
        });
      }
    });
  }

  @Throttle(250)
  public getSingleProviderDXState(): SingleProviderDXState {
    if (!this.executionContext.useSingleProviderDetailedView) {
      return null;
    }
    const hasDX = !!this.setupForm.get('isDx');
    const isDX = this.setupForm.get('isDx')?.value;

    if (!hasDX || isDX) {
      return 'SHOW_CLIENT';
    }
    if (hasDX && !isDX) {
      return 'SHOW_EXECUTE';
    }

    return null;
  }

  public enableHedgeRate(
    executionContext: ExecutionContext,
    val?: boolean
  ): void {
    const hedge = this.setupForm.get('hedge').value;
    const isHedgeCustom = hedge === 'Custom Spot' || hedge === 'Custom Forward';
    const dxValue = val != null ? val : this.setupForm.get('isDx')?.value;

    if (
      isHedgeCustom &&
      executionContext.enableTwoWayCustomHedgeRateForNonDx &&
      this.setupForm.get('hedgeRates') &&
      this.setupForm.get('isDx') &&
      !dxValue
    ) {
      this.setupForm.get('hedgeRates').enable();
      this.setupForm.get('spotRefMethod').enable();
      this.setupForm.get('hedgeRate')?.disable();
    } else if (dxValue || !this.setupForm.get('isDx')) {
      if (isHedgeCustom) {
        this.setupForm.get('hedgeRate')?.enable();
      }
      this.setupForm.get('hedgeRates')?.disable();
    }
  }

  public getUserRfqPreferencesByCurrencyPairAndAccount(
    currencyPair: string,
    account: string
  ): Observable<UserRfqPreferences> {
    const entity = this.entityMapperService.getRealEntity(account);
    const payload = {
      task: 'getUserRfqPreferencesByCurrencyPairAndAccount',
      currencyPair: currencyPair,
      account: entity
    };
    return this.authService.fxPost<UserRfqPreferences>(
      payload,
      'get-user-info',
      {
        task: payload.task
      }
    );
  }

  public changeInExecution(val: string): void {
    ['hedgeRateAsk', 'hedgeRateBid', 'hedgeRateMid'].forEach(field =>
      this.setupForm.get('hedgeRates').get(field).setValue('')
    );
    if (val === 'DX') {
      this.setupForm.get('hedgeRates').get('hedgeRateAsk').disable();
      this.setupForm.get('hedgeRates').get('hedgeRateBid').disable();
      this.setupForm.get('hedgeRates').get('hedgeRateMid').enable();
    } else {
      this.setupForm.get('hedgeRates').get('hedgeRateAsk').enable();
      this.setupForm.get('hedgeRates').get('hedgeRateBid').enable();
      this.setupForm.get('hedgeRates').get('hedgeRateMid').disable();
    }
  }

  public updateStreamedSpotRefs(
    leg: components['schemas']['ClientLegDTO'],
    dp: number
  ): boolean {
    if (
      this.setupForm.get('spotRefMethod')?.value !== 'stream' &&
      this.setupForm.get('execution')
    ) {
      return null;
    }

    const isFwdHedge = this.setupForm.get('hedge').value === 'Forward Hedge';
    const offerValue = isFwdHedge ? leg.forwardOffer : leg.spotOffer;
    const bidValue = isFwdHedge ? leg.forwardBid : leg.spotBid;
    const hedgeRatesCtrl = this.setupForm.get('hedgeRates');

    let changeInValue = false;

    if (offerValue) {
      const formattedOffer = offerValue.toFixed(dp);
      changeInValue =
        changeInValue ||
        (this.setupForm.get('execution').value === 'DX' &&
          formattedOffer !== hedgeRatesCtrl.get('hedgeRateAsk').value);
      hedgeRatesCtrl.get('hedgeRateAsk').setValue(formattedOffer);
    }

    if (bidValue) {
      const formattedBid = bidValue.toFixed(dp);
      changeInValue =
        changeInValue ||
        (this.setupForm.get('execution').value === 'DX' &&
          formattedBid !== hedgeRatesCtrl.get('hedgeRateBid').value);
      hedgeRatesCtrl.get('hedgeRateBid').setValue(formattedBid);
    }

    if (bidValue && offerValue) {
      const mid = (
        (isFwdHedge
          ? leg.forwardBid + leg.forwardOffer
          : leg.spotOffer + leg.spotBid) / 2
      ).toFixed(dp);
      changeInValue =
        changeInValue ||
        (this.setupForm.get('execution').value === 'Live' &&
          mid !== hedgeRatesCtrl.get('hedgeRateMid').value);
      hedgeRatesCtrl.get('hedgeRateMid').setValue(mid);
    }

    return changeInValue;
  }
}
